import classNames from 'classnames';
import { useMemo } from 'react';
import { Button, ModalContentWrapper } from 'ui';
import { CopyIcon } from 'ui/icons';
import { copyTextToClipBoard, fixNameEnum } from 'ui/utils';

type APIMethod = 'delete' | 'post' | 'get' | 'put';
type APIParameterType = 'query' | 'header' | 'body';

interface APIParams {
  name: string;
  example: string;
  required: boolean;
  type: APIParameterType;
}

interface APIItems {
  url: string;
  description: string;
  method: APIMethod;
  params: APIParams[];
}

const SERVER_URL = import.meta.env.VITE_API_URL;

interface IssuerApiKeyModalProps {
  apiKey: string;
  registerId: number;
  open: boolean;
  onClose: () => void;
}

export const IssuerApiKeyModal = ({
  apiKey,
  onClose,
  open,
  registerId,
}: IssuerApiKeyModalProps) => {
  const apiItems: APIItems[] = useMemo(
    () => [
      {
        url: '/v1/sr/embeddable/nav-list',
        method: 'get',
        description: 'Get NAV list by Register Id',
        params: [
          {
            name: 'register_id',
            example: String(registerId),
            required: true,
            type: 'query',
          },
          {
            name: 'from_date',
            example: '2025-01-15',
            required: true,
            type: 'query',
          },
          {
            name: 'to_date',
            example: '2025-02-15',
            required: false,
            type: 'query',
          },
          {
            name: 'Authorization',
            example: 'Bearer {Your API Key}',
            required: true,
            type: 'header',
          },
        ],
      },
      {
        url: '/v1/sr/embeddable/paginated-nav-list',
        method: 'get',
        description: 'Get paginated NAV list by Register Id',
        params: [
          {
            name: 'register_id',
            example: String(registerId),
            required: true,
            type: 'query',
          },
          {
            name: 'register_class_id',
            example: '1',
            required: true,
            type: 'query',
          },
          {
            name: 'page',
            example: '1',
            required: true,
            type: 'query',
          },
          {
            name: 'page_size',
            example: '10',
            required: true,
            type: 'query',
          },
          {
            name: 'Authorization',
            example: 'Bearer {Your API Key}',
            required: true,
            type: 'header',
          },
        ],
      },
    ],
    [registerId],
  );

  return (
    <ModalContentWrapper open={open} title="API key successfully generated" onClose={onClose}>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1 text-[#171717] font-[Inter]">
          <span className="text-[10px] leading-[18px] ">Your API key</span>
          <span
            className="text-[16px] leading-6 font-[500] break-words"
            style={{ wordBreak: 'break-word' }}
          >
            {apiKey}
          </span>
        </div>
        <div className="w-full">
          <Button
            variant="secondary"
            icon={CopyIcon}
            onClick={() =>
              copyTextToClipBoard(apiKey, {
                successMessage: 'API key copied successfully',
              })
            }
          >
            Copy
          </Button>
        </div>
        <div className="w-full flex flex-col gap-3">
          <span className="font-bold text-[16px] leading-6">API Documentation:</span>

          <span className="text-sm">
            Base URL: <span className="text-gray-500">{SERVER_URL}</span>
          </span>

          <div className="w-full flex flex-col gap-3 overflow-y-auto max-h-[420px] pr-1">
            {apiItems.map((item, i) => (
              <div
                key={i}
                className={classNames(
                  'flex flex-col w-full border rounded-lg py-2 gap-2 font-[Inter]',
                  {
                    'bg-red-50': item.method === 'delete',
                    'bg-green-50': item.method === 'get',
                    'bg-blue-50': item.method === 'post',
                    'bg-amber-50': item.method === 'put',
                  },
                )}
              >
                <span className="flex gap-2 items-center border-b px-2 pb-2">
                  <span
                    className={classNames(
                      'border rounded px-2 text-white uppercase text-center w-[80px]',
                      {
                        'bg-red-600': item.method === 'delete',
                        'bg-green-600': item.method === 'get',
                        'bg-blue-600': item.method === 'post',
                        'bg-amber-600': item.method === 'put',
                      },
                    )}
                  >
                    {fixNameEnum(item.method)}
                  </span>
                  <span
                    className="cursor-pointer hover:underline"
                    title="Click to copy URL"
                    onClick={() =>
                      copyTextToClipBoard(SERVER_URL + item.url, {
                        successMessage: 'URL copied successfully',
                      })
                    }
                  >
                    {item.url}
                  </span>
                  <span className="text-sm text-gray-600">{item.description}</span>
                </span>
                <span className="px-4 flex flex-col gap-6">
                  <span>Parameters:</span>
                  <div className="w-full flex flex-col gap-4">
                    <span className="w-[350px] flex flex-row justify-between gap-4 text-xs pb-2 border-b">
                      <span>Name</span>
                      <span>Value</span>
                    </span>
                    {item.params.map((param, p) => (
                      <span
                        key={'param_' + p}
                        className="w-[350px] flex flex-row justify-between gap-4"
                      >
                        <span className="flex flex-col">
                          <span className="font-bold text-sm">{param.name}</span>
                          <span className="text-[13px] leading-5">({param.type})</span>
                          {param.required && (
                            <span className="text-xs text-red-500">* required</span>
                          )}
                        </span>
                        <span>{param.example}</span>
                      </span>
                    ))}
                  </div>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </ModalContentWrapper>
  );
};
